/**
 * offsetOverlapDetection — unified overlap detection for adapter offset matrices.
 *
 * Pure TypeScript, framework-free. No React, no antd. MessageContent values
 * still arrive as plain numbers, converted by callers.
 *
 * The one generated type imported here is `KeyDirection`, which is a bare
 * TypeScript enum with no runtime dependency on the FlatBuffers library — so it
 * costs this module nothing in purity, and the workspace rule against
 * re-spelling a generated enum as local magic numbers applies with full force
 * to a discriminant whose values are a wire contract.
 */
import { parseCompositeKey } from "../../../flatbuffers/types/ln2MessageOptions";
import { KeyDirection } from "../../../flatbuffers/generated/archive/key-direction";
import type { MatrixKeyMappingSubset } from "./AdapterOffsetMatrix";
import { columnOccupancy, rangesActuallyCollide } from "./categoryOccupancy";
import { widthOf } from "./adapterSizeMath";

// ---------------------------------------------------------------------------
// Public interfaces
// ---------------------------------------------------------------------------

export interface ResolvedAddress {
  unitIdx: number;
  absByte: number;
  /** Bit position within the byte, -1 for byte-mode. */
  bit: number;
  colKey: string;
  label: string;
  size: number;
  mcNumber: number;
}

export interface OverlapInfo {
  a: ResolvedAddress;
  b: ResolvedAddress;
  overlappingBytes: number[];
  /** Populated when bit-mode is relevant. See §1.7 for full semantic. */
  overlappingBits?: number[];
  /** True when both participants carry intra-content=true on the same unit
   *  AND come from different MessageContent categories. */
  intraAllowed: boolean;
  /** True when a and b are on different unit indices (cross-unit bleed). */
  crossUnit: boolean;
}

export type CategoryRegTypeMap = Record<string, string>;

/** Lookup function; callers MUST implement via `intraLookup` helper. */
export type IntraContentLookup = (mcNumber: number, unitIdx: number) => boolean;

export interface MatrixColumnSpec {
  colKey: string;
  label: string;
  size: number;
  mcNumber: number;
  disabled?: boolean;
  regType?: string;
  /** True when the column applies uniformly across the whole bank (no
   *  per-car delta). Used to SKIP cross-lift bleed when same colKey
   *  appears on both lifts (legitimate shared register). */
  bankLevel?: boolean;
  /** True when the column is a ByteMessage floor expansion: value varies
   *  per floor, offset stays fixed. Used to SKIP cross-lift bleed
   *  independently of bankLevel. */
  isByteExpansion?: boolean;
  /** True when the column's category is a group-shared register (one register
   *  read/written identically for every car). Skips same-colKey cross-car
   *  bleed like bankLevel. When undefined, the detector falls back to
   *  isGroupScopedCategory(colKey) so group categories are shared by default.
   *  An explicit `false` (operator turned the toggle off) re-enables flagging. */
  groupShared?: boolean;
  /** The single LN1 map every key in this column claims, when they agree.
   *
   *  Derived from the column's rows by `deriveColumnDirection`, NOT authored:
   *  direction is a property of a key, and a column is only single-directional
   *  if every key in it points the same way. `undefined` means mixed, or no
   *  keys, or at least one key at BOTH — all of which leave the column claiming
   *  both maps and colliding exactly as before.
   *
   *  Two columns landing on the same absolute byte are a real conflict only if
   *  they claim the same map. On a Swift CEC, Control and Traffic legitimately
   *  share byte 8: one is written to the controller, the other read from it. */
  direction?: KeyDirection;
}

export interface MatrixKeySpec {
  colKey: string;
  label: string;
  mcNumber: number;
  byteOffset: number;
  bit: number;
  regType?: string;
}

export interface MappingRowForDetection {
  contentType: "bit" | "byte";
  byte_offset: number;
  bit_offset: number;
  data: boolean;
  offset: number;
  value: number;
  bank_level: boolean;
  individual: boolean;
  ln2MessageKey: string;
  is_pair_secondary: boolean;
  floors_based: boolean;
  /** Which of the two LN1 maps this row feeds. Absent is read as BOTH. */
  direction?: KeyDirection;
}

export interface MappingRowDetectionInput {
  mappings: ReadonlyArray<MappingRowForDetection>;
  adapterMatrix: Record<string, { initial: number; delta: number }>;
  bankMatrixPerLift: ReadonlyArray<Record<string, number>>;
  numCars: number;
  intra: IntraContentLookup;
  regTypes?: CategoryRegTypeMap;
  /** Set when the adapter has opted into per-key direction. While false, every
   *  row is treated as BOTH and detection behaves exactly as it did before
   *  direction existed. */
  directionAware?: boolean;
}

/** A row feeds offset_to_msg (device → LN2) unless it is write-only. */
const feedsRead = (row: MappingRowForDetection): boolean =>
  (row.direction ?? KeyDirection.BOTH) !== KeyDirection.TO_DEVICE;

/** A row feeds msg_to_offset (LN2 → device) unless it is read-only. */
const feedsWrite = (row: MappingRowForDetection): boolean =>
  (row.direction ?? KeyDirection.BOTH) !== KeyDirection.FROM_DEVICE;

/**
 * Whether every row sharing one address claims a different LN1 map, so none of
 * them actually collide.
 *
 * This is the same two-counter rule the server enforces
 * (`ValidateKeyDirections` in LiftCloud's key_direction.go) — deliberately, so
 * the editor and the API cannot disagree about what is legal. A row occupies a
 * map iff its direction feeds it, so BOTH occupies both and still collides with
 * anything else on the address.
 *
 * With direction off, or with every row at BOTH, a bucket of N rows yields
 * read = write = N, so any N > 1 is still a collision and the pre-existing
 * detections are untouched.
 */
function bucketClaimsAreDisjoint(rows: ReadonlyArray<MappingRowForDetection>): boolean {
  const read = rows.filter(feedsRead).length;
  const write = rows.filter(feedsWrite).length;

  return read <= 1 && write <= 1;
}

/**
 * Whether two columns landing on the same byte claim opposite LN1 maps, and so
 * do not actually conflict.
 *
 * The column-level counterpart of `bucketClaimsAreDisjoint`. It is deliberately
 * strict: only an explicit TO_DEVICE against an explicit FROM_DEVICE is
 * excused. `undefined` on either side means that column has mixed directions,
 * no keys, or a key at BOTH — in every one of those it still claims both maps,
 * so the overlap is real and stays flagged.
 *
 * This is self-gating rather than switched on an adapter flag: with no key
 * carrying a direction, every column derives `undefined` and detection is
 * byte-identical to what it was before direction existed.
 */
export function columnDirectionsAreOpposed(a: MatrixColumnSpec, b: MatrixColumnSpec): boolean {
  if (a.direction === undefined || b.direction === undefined) return false;
  if (a.direction === KeyDirection.BOTH || b.direction === KeyDirection.BOTH) return false;

  return a.direction !== b.direction;
}

/**
 * Directions claimed by the keys of one column that actually sit in a byte
 * range — not the directions of the whole column.
 *
 * `columnDirectionsAreOpposed` asks a column-wide question, and that is too
 * coarse whenever a column stands for a whole CATEGORY. A category column
 * aggregates every key of that MessageContent, so one `BOTH` key anywhere in it
 * collapses the column's direction to `undefined` and no overlap it takes part
 * in can ever be excused -- even when the two keys that actually share the
 * disputed byte are cleanly split, one writing and one reading. That is the
 * shape the direction feature exists to make legal, and on a category column it
 * was unreachable.
 *
 * Byte positions are resolved the way `spanOf` measures a span: relative to the
 * column's FIRST register, because the matrix cell holds the address of that
 * first register and every other key of the column sits at a fixed distance
 * from it.
 */
function directionsInByteRange(
  mappings: MatrixKeyMappingSubset | undefined,
  colKey: string,
  colBase: number,
  lo: number,
  hi: number,
  floors?: number,
): Set<KeyDirection> | undefined {
  if (!mappings || mappings.length === 0) return undefined;

  const rows = mappings.filter((row) => {
    if (row.is_pair_secondary) return false;
    if (row.ln2MessageKey === colKey) return true;
    return row.ln2MessageKey.split(":")[0] === colKey;
  });
  if (rows.length === 0) return undefined;

  const byteOf = (row: (typeof rows)[number]): number =>
    row.contentType === "bit" ? row.byte_offset : row.offset;
  const min = rows.reduce((acc, row) => Math.min(acc, byteOf(row)), Number.POSITIVE_INFINITY);

  // A floor-expanded bit mask spreads one bit per floor over `ceil(floors / 8)`
  // bytes, so placing it at its byte_offset alone puts it outside a range it
  // genuinely sits in. Control::SecureCarCall on the BWH RVU is three bytes per
  // car; measured as one, it was absent from the very range it collides over
  // and this function returned "no keys here" for the column being judged.
  const claimed = new Set<KeyDirection>();
  rows.forEach((row) => {
    const first = colBase + (byteOf(row) - min);
    const width = floors && floors > 0 ? widthOf(row, floors) : 1;
    for (let w = 0; w < width; w += 1) {
      const abs = first + w;
      if (abs >= lo && abs < hi) claimed.add((row.direction ?? KeyDirection.BOTH) as KeyDirection);
    }
  });

  return claimed.size > 0 ? claimed : undefined;
}

/**
 * True when the keys sharing the disputed bytes are split one-way each.
 *
 * Strict in the same way `columnDirectionsAreOpposed` is: a single `BOTH`, or a
 * mix, on either side means that side still claims both maps and the overlap is
 * real. Falling back to this only AFTER the column-level test has declined
 * means it can only ever excuse more, never flag more.
 */
function byteRangeDirectionsAreOpposed(
  mappings: MatrixKeyMappingSubset | undefined,
  colAKey: string,
  colABase: number,
  colBKey: string,
  colBBase: number,
  lo: number,
  hi: number,
  floors?: number,
): boolean {
  const a = directionsInByteRange(mappings, colAKey, colABase, lo, hi, floors);
  const b = directionsInByteRange(mappings, colBKey, colBBase, lo, hi, floors);
  if (!a || !b || a.size !== 1 || b.size !== 1) return false;

  const [da] = [...a];
  const [db] = [...b];
  if (da === KeyDirection.BOTH || db === KeyDirection.BOTH) return false;

  return da !== db;
}

/**
 * Whether two columns whose byte RANGES overlap in fact claim no byte in common.
 *
 * A column's range is the bounding box of its messages, which is the right
 * question for "could these collide" and the wrong one for "do they". A
 * category column aggregating messages at relative bytes 0 and 9 spans ten
 * bytes and occupies two, so Status at 100 and Traffic at 105 are reported as
 * colliding on 105..109 when neither has a register anywhere in there. The
 * operator is sent to move an offset that was already right, and learns to
 * disregard the red.
 *
 * Asked only AFTER the range test has already failed, and only ever excusing,
 * so a column whose messages fill its range behaves exactly as it did. It
 * declines outright — leaving the overlap flagged — whenever it cannot measure
 * safely: no mappings at all, a column no mapping matches, or a floor-expanded
 * mask with no floor count to size it by. See `columnOccupancy`.
 *
 * A per-message column is one message, so its occupancy IS its range and this
 * can never excuse it. The refinement only bites on category columns, which is
 * the only place the bounding box was ever loose.
 */
function occupancyExcusesOverlap(
  keyMappings: MatrixKeyMappingSubset | undefined,
  floors: number | undefined,
  aColKey: string,
  aStart: number,
  bColKey: string,
  bStart: number,
): boolean {
  const a = columnOccupancy(keyMappings, aColKey, floors ?? 0, aStart);
  const b = columnOccupancy(keyMappings, bColKey, floors ?? 0, bStart);
  if (!a || !b) return false;

  return !rangesActuallyCollide(a, b);
}

// ---------------------------------------------------------------------------
// OVERLAP_STYLE — moved from CdoLn1MappingEditor.tsx
// ---------------------------------------------------------------------------

export const OVERLAP_STYLE: {
  boxShadow: string;
  borderColor: string;
  borderRadius: number;
} = {
  boxShadow: "0 0 8px 2px rgba(255, 77, 79, 0.6)",
  borderColor: "#ff4d4f",
  borderRadius: 4,
};

// ---------------------------------------------------------------------------
// intraLookup — handles both number-keyed and JSON-coerced string-keyed records
// ---------------------------------------------------------------------------

/** Handles both number-keyed and JSON-coerced string-keyed records. */
export function intraLookup(
  flags: Partial<Record<number | string, boolean[]>> | undefined,
  mcNumber: number,
  unitIdx: number,
): boolean {
  if (!flags) return false;
  const arr =
    (flags as Record<number, boolean[]>)[mcNumber] ??
    (flags as Record<string, boolean[]>)[String(mcNumber)];
  return Array.isArray(arr) ? arr[unitIdx] === true : false;
}

// ---------------------------------------------------------------------------
// categoryKeyToMcNumber
// ---------------------------------------------------------------------------

export function categoryKeyToMcNumber(catKey: string): number {
  const stripped = catKey.split(":")[0] ?? "";
  switch (stripped) {
    case "status":
      return 1;
    case "traffic":
      return 2;
    case "usage":
      return 3;
    case "service":
      return 4;
    case "control":
      return 5;
    case "fault":
      return 6;
    case "group_supervisory_status":
      return 7;
    case "group_control":
      return 8;
    case "controller_info":
      return 9;
    default:
      return 0;
  }
}

/**
 * True for MessageContent categories that address the whole group from a
 * single register on every car — Group Supervisory Status and Group Control.
 *
 * These are group-scoped by definition: one shared register (car delta 0) is
 * read/written identically for every car, so the same address appearing on
 * multiple cars is a legitimate shared register, never a cross-car "bleed".
 * Overlap detection therefore treats them like explicit bank-level columns
 * and skips the same-column cross-car check (distinct-category collisions are
 * still flagged).
 */
export function isGroupScopedCategory(colKey: string): boolean {
  const stripped = colKey.split(":")[0] ?? "";
  // Two category-key spellings exist for group-supervisory: the offset matrix
  // uses "group_supervisory_status", ln2MessageOptions uses "group_supervisory".
  // Match both (startsWith), plus group_control.
  return stripped.startsWith("group_supervisory") || stripped === "group_control";
}

// ---------------------------------------------------------------------------
// mkAddrKey — unified address-key composer
// ---------------------------------------------------------------------------

export function mkAddrKey(
  row: Pick<MappingRowForDetection, "contentType" | "bit_offset" | "value" | "data">,
  absByte: number,
  unitIdx?: number,
): string {
  const unitPart = unitIdx === undefined ? "*" : String(unitIdx);
  if (row.contentType === "bit") {
    return `${absByte}:bit${row.bit_offset}:${row.data}:${unitPart}`;
  }
  return `${absByte}:byte:${row.value}:${unitPart}`;
}

// ---------------------------------------------------------------------------
// expandKeyAddresses
// ---------------------------------------------------------------------------

export function expandKeyAddresses(
  col: MatrixColumnSpec,
  initialOffset: number,
  carDelta: number,
  numCars: number,
): ResolvedAddress[] {
  return Array.from({ length: numCars }, (_, u) => ({
    unitIdx: u,
    absByte: initialOffset + u * carDelta,
    bit: -1,
    colKey: col.colKey,
    label: col.label,
    size: col.size,
    mcNumber: col.mcNumber,
  }));
}

// ---------------------------------------------------------------------------
// resolveColumnAddresses
// ---------------------------------------------------------------------------

/**
 * `unitIdx` for a register that belongs to no single car.
 *
 * A column with a car delta of 0 is ONE physical register that every car reads.
 * Giving it a car number is not a rounding error, it is a fabrication: the
 * report would name a car that has no register of its own, and — because the
 * fabricated indices differ — would tag the result CROSS-CAR, which is the
 * marker reserved for a car silently overwriting another car's data.
 */
export const SHARED_UNIT = -1;

/**
 * True when a collision of this column WITH ITSELF across cars is legitimate.
 *
 * These are the three cases `detectBankMatrixOverlaps` has always guarded its
 * own same-column cross-lift comparison with, and they are guarded here for the
 * same reason: a bank-level mapping, a group-shared category and a ByteMessage
 * floor expansion all put the same address on every car on purpose.
 *
 * Note what this does NOT do: it never removes the column from comparison
 * against OTHER columns. A column excluded outright would stop being checked
 * against everything else on the matrix, which is a bigger hole than the one
 * being closed.
 */
function sharesAddressAcrossCars(col: MatrixColumnSpec): boolean {
  if (col.bankLevel === true) return true;
  if (col.isByteExpansion === true) return true;
  return col.groupShared ?? isGroupScopedCategory(col.colKey);
}

/**
 * The DISTINCT physical addresses a column occupies, one entry per address.
 *
 * `expandKeyAddresses` answers "what does car u read", which is the right
 * question for the bank matrix, where every lift has its own offset. It is the
 * wrong question here: with a car delta of 0 it returns `numCars` copies of one
 * address, and every pair of those copies then reads as a separate collision
 * between two different cars. A freshly created adapter, whose columns all sit
 * at the 0/0 default, produced `numCars`² occurrences per column pair that way
 * — 108 rows for three columns and six cars, 90 of them wearing the CROSS-CAR
 * tag — and buried whatever real collision was underneath.
 *
 * The test is the arithmetic, not the flag. A delta of 0 means the addresses
 * ARE the same address; a bank-level column whose stored delta is somehow not 0
 * really does put different bytes on different cars, and collapsing it on the
 * strength of the flag would stop those bytes being compared with anything.
 * Same-column collisions are excused by flag, one step further on.
 */
export function resolveColumnAddresses(
  col: MatrixColumnSpec,
  initialOffset: number,
  carDelta: number,
  numCars: number,
): ResolvedAddress[] {
  if (numCars <= 0) return [];
  // With a single car there is no fan-out to collapse and no car identity to
  // invent — one address either way, and it really is that car's.
  if (numCars === 1 || carDelta !== 0) {
    return expandKeyAddresses(col, initialOffset, carDelta, numCars);
  }
  return [
    {
      unitIdx: SHARED_UNIT,
      absByte: initialOffset,
      bit: -1,
      colKey: col.colKey,
      label: col.label,
      size: col.size,
      mcNumber: col.mcNumber,
    },
  ];
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * True when the two addresses land on the same car, for intra-content purposes.
 *
 * A shared register lands on EVERY car, so it is on the same car as whichever
 * per-car register it collides with. Treating `SHARED_UNIT` as a car of its own
 * would withdraw the intra-content excuse from bank-level columns that have
 * always had it.
 */
function sameCarForIntra(a: ResolvedAddress, b: ResolvedAddress): boolean {
  if (a.unitIdx === b.unitIdx) return true;
  return a.unitIdx < 0 || b.unitIdx < 0;
}

/** The unit whose intra-content flags govern this pair. */
function intraUnitIndex(a: ResolvedAddress, b: ResolvedAddress): number {
  if (a.unitIdx >= 0) return a.unitIdx;
  if (b.unitIdx >= 0) return b.unitIdx;
  return 0;
}

function pushInfo(map: Map<string, OverlapInfo[]>, key: string, info: OverlapInfo): void {
  const arr = map.get(key);
  if (arr) {
    arr.push(info);
    return;
  }
  map.set(key, [info]);
}

function rangeOverlapBytes(aStart: number, aSize: number, bStart: number, bSize: number): number[] {
  const aEnd = aStart + aSize;
  const bEnd = bStart + bSize;
  if (!(aStart < bEnd && bStart < aEnd)) return [];
  const lo = Math.max(aStart, bStart);
  const hi = Math.min(aEnd, bEnd) - 1;
  return Array.from({ length: hi - lo + 1 }, (_, k) => lo + k);
}

// ---------------------------------------------------------------------------
// detectMatrixOverlaps
// ---------------------------------------------------------------------------

export function detectMatrixOverlaps(
  columns: MatrixColumnSpec[],
  matrixValues: Record<string, { initial: number; delta: number }>,
  numCars: number,
  intra: IntraContentLookup,
  regTypes?: CategoryRegTypeMap,
  // Optional and last so every existing caller is unaffected. Together they let
  // the detector ask what a column's messages actually occupy instead of
  // trusting its bounding box; absent, or unable to measure, it keeps flagging
  // on the box exactly as before. `detectBankMatrixOverlaps` takes the same two
  // for the same reason, so the two screens refine identically or not at all.
  keyMappings?: MatrixKeyMappingSubset,
  floors?: number,
): Map<string, OverlapInfo[]> {
  const result = new Map<string, OverlapInfo[]>();
  const allAddresses: ResolvedAddress[] = [];
  /** Columns whose collision with THEMSELVES across cars is by design. */
  const sharedColumns = new Set<string>();
  /** colKey -> spec, so the pair loop below can read each column's direction. */
  const specByKey = new Map<string, MatrixColumnSpec>();

  columns.forEach((col) => {
    if (col.disabled) return;
    specByKey.set(col.colKey, col);
    if (sharesAddressAcrossCars(col)) sharedColumns.add(col.colKey);
    const vals = matrixValues[col.colKey] ?? { initial: 0, delta: 0 };
    const addrs = resolveColumnAddresses(col, vals.initial, vals.delta, numCars);
    addrs.forEach((addr) => {
      allAddresses.push(addr);
    });
  });

  allAddresses.forEach((a, i) => {
    allAddresses.slice(i + 1).forEach((b) => {
      if (a.colKey === b.colKey && sharedColumns.has(a.colKey)) return;
      // Deliberately NOT restricted to a.unitIdx === b.unitIdx, and — apart from
      // the shared columns just excused — NOT restricted to a.colKey !== b.colKey.
      //
      // The same-column skip was unconditional, applied before any range math,
      // so column X on car 1 was never compared with column X on car 2. That
      // hides the archetypal delta mistake: a car stride SMALLER than the
      // register it strides over. Status is 8 bytes wide with a Car Delta of 4,
      // car 1 owns [0, 8), car 2 owns [4, 12), and bytes 4..7 are claimed twice
      // — five genuine cross-car collisions on a six-car bank, and the screen
      // said "no overlapping offsets". `detectBankMatrixOverlaps` has always
      // compared a column against itself across lifts, guarding it narrowly
      // rather than skipping it wholesale; the shared-register cases that guard
      // exists for are collapsed to a single address by
      // `resolveColumnAddresses` before they reach here.
      //
      // This used to return early on differing units, so a byte claimed by one
      // car under one category and a DIFFERENT car under another was never even
      // compared. That is not a hypothetical: on a real six-car RVU site,
      // Traffic:normal:0 starts at 131 with a 23-byte car stride while
      // Control:normal:9 starts at 168 with a 16-byte one, and they meet exactly
      // at byte 200 — car 4's position register and car 3's control base. The
      // CDO builder's offset->car map is keyed on the byte alone, so one of them
      // silently won and car 3's dashboard position became car 4's. The editor
      // had the data to catch it and declined to look.
      //
      // `detectBankMatrixOverlaps` has done the cross-lift comparison since it
      // was written; this brings the adapter matrix in line with it.
      if (regTypes?.[a.colKey] && regTypes?.[b.colKey] && regTypes[a.colKey] !== regTypes[b.colKey])
        return;

      // Two columns can legitimately sit on one byte when one is only ever
      // WRITTEN to the controller and the other only ever READ from it — the
      // Swift CEC case, where byte 8 energizes an up hall call outbound and
      // reports a down hall call inbound. The runtime keeps offset_to_msg and
      // msg_to_offset as two independent maps, so the byte is looked up in one
      // or the other depending on which way the frame travels; it is never
      // ambiguous. Only an explicit TO_DEVICE against an explicit FROM_DEVICE
      // is excused, so a column with any BOTH or mixed-direction key still
      // collides.
      const specA = specByKey.get(a.colKey);
      const specB = specByKey.get(b.colKey);
      if (specA && specB && columnDirectionsAreOpposed(specA, specB)) return;

      const aStart = a.absByte;
      const aEnd = a.absByte + a.size;
      const bStart = b.absByte;
      const bEnd = b.absByte + b.size;
      if (!(aStart < bEnd && bStart < aEnd)) return;

      // The column-level test declined. Ask the narrower question before
      // flagging: are the keys that actually sit on the disputed bytes split
      // one-way each?
      //
      // `detectBankMatrixOverlaps` and the bank editor have asked this since
      // #1734; this entry point never did, so setting ONE key of a category to
      // Both flagged every overlap that category takes part in -- even where
      // the keys on the disputed bytes are cleanly split. On the BWH RVU that
      // is Status::Heartbeat: it sits at byte 141, nowhere near
      // Control::SecureCarCall's 119-136, yet marking it Both made the whole
      // Status column mixed and the Secure Car Call overlap appear out of
      // nowhere. Same divergence as the comment at the equivalent point in
      // `detectBankMatrixOverlaps`, in the other direction.
      //
      // The base passed for each side is that side's base FOR ITS OWN CAR, not
      // the column's first-car base. A column repeats every `delta` bytes, and
      // the overlap being judged here is routinely between two different cars
      // -- Secure Car Call car 4 against Status car 0 on the BWH RVU. Handing
      // over the first-car base would place the column's keys at addresses the
      // disputed range does not contain, the function would find no keys, and
      // it would decline to excuse anything it is asked about across cars.
      const carBase = (addr: ResolvedAddress): number => {
        const vals = matrixValues[addr.colKey];
        if (!vals) return 0;
        return vals.initial + vals.delta * addr.unitIdx;
      };
      if (
        byteRangeDirectionsAreOpposed(
          keyMappings,
          a.colKey,
          carBase(a),
          b.colKey,
          carBase(b),
          Math.max(aStart, bStart),
          Math.min(aEnd, bEnd),
          floors,
        )
      )
        return;

      // The ranges overlap. Do the messages inside them actually share a byte?
      // A category column's range is the bounding box of its messages, and two
      // boxes can intersect over bytes neither column puts a register on.
      if (occupancyExcusesOverlap(keyMappings, floors, a.colKey, aStart, b.colKey, bStart)) return;

      const loByte = Math.max(aStart, bStart);
      const hiByte = Math.min(aEnd, bEnd) - 1;
      const overlappingBytes = Array.from({ length: hiByte - loByte + 1 }, (_, k) => loByte + k);

      let overlappingBits: number[] | undefined;
      if (a.bit >= 0 && b.bit >= 0 && aStart === bStart) {
        if (a.bit === b.bit) {
          overlappingBits = [a.bit];
        } else {
          overlappingBits = [a.bit, b.bit];
        }
      }

      const intraUnit = intraUnitIndex(a, b);
      const intraAllowed =
        sameCarForIntra(a, b) &&
        a.mcNumber !== b.mcNumber &&
        intra(a.mcNumber, intraUnit) &&
        intra(b.mcNumber, intraUnit);

      const info: OverlapInfo = {
        a,
        b,
        overlappingBytes,
        overlappingBits,
        intraAllowed,
        // Reported honestly rather than hardcoded, so the tooltip can name the
        // two cars. formatOverlapTooltip already renders this as
        // "cross-lift bleed from lift N into lift M".
        //
        // A shared register carries SHARED_UNIT and belongs to no car, so it is
        // never cross-car: it cannot bleed from one car into another when it is
        // the same register on both.
        crossUnit: a.unitIdx >= 0 && b.unitIdx >= 0 && a.unitIdx !== b.unitIdx,
      };

      if (!intraAllowed) {
        pushInfo(result, `${a.colKey}:${a.unitIdx}`, info);
        pushInfo(result, `${b.colKey}:${b.unitIdx}`, info);
      }
    });
  });

  return result;
}

// ---------------------------------------------------------------------------
// detectBankMatrixOverlaps
// ---------------------------------------------------------------------------

export function detectBankMatrixOverlaps(
  columns: MatrixColumnSpec[],
  perLiftOffsets: ReadonlyArray<Record<string, number>>,
  intra: IntraContentLookup,
  regTypes?: CategoryRegTypeMap,
  keysPerColumn?: Record<string, MatrixKeySpec[]>,
  crossLiftIntraBehavior?: "always-flag" | "respect-intra",
  // Optional and last so every existing caller is unaffected. Absent means the
  // column-level direction test is the only one, which is exactly the previous
  // behaviour.
  keyMappings?: MatrixKeyMappingSubset,
  // Needed to size a floor-expanded bit mask. Without it the occupancy check
  // declines rather than measuring a mask spanning ceil(floors / 8) bytes as
  // one byte -- see `columnOccupancy`.
  floors?: number,
): Map<string, OverlapInfo[]> {
  const result = new Map<string, OverlapInfo[]>();

  // Pass 1 compares ResolvedAddresses, which carry colKey but not the column's
  // direction, so resolve back to the spec. An unknown colKey yields an empty
  // spec whose direction is undefined — which columnDirectionsAreOpposed treats
  // as "claims both maps", i.e. it still flags. Failing closed is the right
  // default for a collision check.
  const specByColKey = new Map(columns.map((c) => [c.colKey, c]));
  const byColKey = (colKey: string): MatrixColumnSpec =>
    specByColKey.get(colKey) ??
    ({ colKey, label: colKey, size: 1, mcNumber: 0 } as MatrixColumnSpec);

  // Pass 1: intra-lift pairwise
  perLiftOffsets.forEach((liftOffsets, liftIdx) => {
    const addrs: ResolvedAddress[] = [];
    columns.forEach((col) => {
      if (col.disabled) return;
      const liftOffset = liftOffsets[col.colKey] ?? 0;
      const expanded = expandKeyAddresses(col, liftOffset, 0, 1);
      expanded.forEach((addr) => {
        addrs.push({ ...addr, unitIdx: liftIdx });
      });
    });

    addrs.forEach((a, i) => {
      addrs.slice(i + 1).forEach((b) => {
        if (a.colKey === b.colKey) return;
        if (
          regTypes?.[a.colKey] &&
          regTypes?.[b.colKey] &&
          regTypes[a.colKey] !== regTypes[b.colKey]
        )
          return;

        // Opposite directions are not a collision. detectMatrixOverlaps has
        // applied this since direction landed (see its call at the equivalent
        // point); this entry point never did, so the ADAPTER page excused a
        // Control/Traffic pair sharing one byte while the BANK page flagged it
        // — the two screens disagreeing about the same keys, which is exactly
        // what the direction feature exists to make legal.
        if (columnDirectionsAreOpposed(byColKey(a.colKey), byColKey(b.colKey))) return;

        const aStart = a.absByte;
        const aEnd = a.absByte + a.size;
        const bStart = b.absByte;
        const bEnd = b.absByte + b.size;
        if (!(aStart < bEnd && bStart < aEnd)) return;

        // The column-level test declined. Ask the narrower question before
        // flagging: are the keys that actually sit on the disputed bytes split
        // one-way each? A CATEGORY column aggregates every key of its
        // MessageContent, so one BOTH key anywhere in it hides a clean split on
        // the bytes that genuinely collide.
        if (
          byteRangeDirectionsAreOpposed(
            keyMappings,
            a.colKey,
            liftOffsets[a.colKey] ?? 0,
            b.colKey,
            liftOffsets[b.colKey] ?? 0,
            Math.max(aStart, bStart),
            Math.min(aEnd, bEnd),
          )
        )
          return;

        // Same question the adapter matrix asks at this point: the boxes
        // intersect, but do the messages inside them share a byte?
        if (
          occupancyExcusesOverlap(
            keyMappings,
            floors,
            a.colKey,
            liftOffsets[a.colKey] ?? 0,
            b.colKey,
            liftOffsets[b.colKey] ?? 0,
          )
        )
          return;

        const overlappingBytes = rangeOverlapBytes(aStart, a.size, bStart, b.size);

        const intraAllowed =
          a.mcNumber !== b.mcNumber && intra(a.mcNumber, liftIdx) && intra(b.mcNumber, liftIdx);

        const info: OverlapInfo = {
          a,
          b,
          overlappingBytes,
          intraAllowed,
          crossUnit: false,
        };

        if (!intraAllowed) {
          pushInfo(result, `${a.colKey}:${liftIdx}`, info);
          pushInfo(result, `${b.colKey}:${liftIdx}`, info);
        }
      });
    });
  });

  // Pass 2: cross-lift bleed
  perLiftOffsets.slice(0, -1).forEach((_, liftA) => {
    perLiftOffsets.slice(liftA + 1).forEach((__, liftBRel) => {
      const liftB = liftA + 1 + liftBRel;
      columns.forEach((colA) => {
        if (colA.disabled) return;
        columns.forEach((colB) => {
          if (colB.disabled) return;
          // Guard 1: same-colKey shared register. Either explicitly bank-level,
          // or a group-shared category whose one register is shared by every
          // car (car delta 0) — the same address on two cars is a legitimate
          // share. group_shared defaults on for group-scoped content types
          // (Group Supervisory Status / Group Control) but is operator-
          // overridable per mapping; an explicit false re-enables flagging.
          const aGroupShared = colA.groupShared ?? isGroupScopedCategory(colA.colKey);
          if (colA.colKey === colB.colKey && (colA.bankLevel === true || aGroupShared)) return;
          // Guard 2: same-colKey ByteMessage expansion
          if (colA.colKey === colB.colKey && colA.isByteExpansion === true) return;
          // Register-type guard
          if (
            regTypes?.[colA.colKey] &&
            regTypes?.[colB.colKey] &&
            regTypes[colA.colKey] !== regTypes[colB.colKey]
          )
            return;

          // Same excuse as pass 1, for the cross-lift comparison.
          if (columnDirectionsAreOpposed(colA, colB)) return;

          const aStart = perLiftOffsets[liftA][colA.colKey] ?? 0;
          const aEnd = aStart + colA.size;
          const bStart = perLiftOffsets[liftB][colB.colKey] ?? 0;
          const bEnd = bStart + colB.size;
          if (!(aStart < bEnd && bStart < aEnd)) return;

          // Same narrower question as pass 1, for the cross-lift comparison.
          if (
            byteRangeDirectionsAreOpposed(
              keyMappings,
              colA.colKey,
              aStart,
              colB.colKey,
              bStart,
              Math.max(aStart, bStart),
              Math.min(aEnd, bEnd),
            )
          )
            return;

          // Same question again, for the cross-lift comparison.
          if (
            occupancyExcusesOverlap(keyMappings, floors, colA.colKey, aStart, colB.colKey, bStart)
          )
            return;

          const behavior = crossLiftIntraBehavior ?? "always-flag";
          let intraAllowed = false;
          if (behavior === "respect-intra") {
            intraAllowed =
              colA.mcNumber !== colB.mcNumber &&
              intra(colA.mcNumber, liftA) &&
              intra(colB.mcNumber, liftB);
          }

          const overlappingBytes = rangeOverlapBytes(aStart, colA.size, bStart, colB.size);
          const info: OverlapInfo = {
            a: {
              unitIdx: liftA,
              absByte: aStart,
              bit: -1,
              colKey: colA.colKey,
              label: colA.label,
              size: colA.size,
              mcNumber: colA.mcNumber,
            },
            b: {
              unitIdx: liftB,
              absByte: bStart,
              bit: -1,
              colKey: colB.colKey,
              label: colB.label,
              size: colB.size,
              mcNumber: colB.mcNumber,
            },
            overlappingBytes,
            intraAllowed,
            crossUnit: true,
          };

          if (!intraAllowed) {
            pushInfo(result, `${colA.colKey}:${liftA}`, info);
            pushInfo(result, `${colB.colKey}:${liftB}`, info);
          }
        });
      });
    });
  });

  return result;
}

// ---------------------------------------------------------------------------
// Internal helpers for detectMappingRowOverlaps
// ---------------------------------------------------------------------------

function intraForRow(row: MappingRowForDetection, intra: IntraContentLookup): boolean {
  const parsed = parseCompositeKey(row.ln2MessageKey);
  if (!parsed) return false;
  const mcN = categoryKeyToMcNumber(parsed.categoryKey);
  return intra(mcN, 0);
}

function resolveColKey(row: MappingRowForDetection, parsedCategoryKey: string): string {
  if (row.ln2MessageKey.includes(":")) return row.ln2MessageKey;
  return parsedCategoryKey;
}

function checkAdapterMatrixResolves(
  rows: ReadonlyArray<MappingRowForDetection>,
  adapterMatrix: Record<string, { initial: number; delta: number }>,
  numCars: number,
): boolean {
  // For individual rows, resolution means each car (unit) gets a DISTINCT
  // absolute address for the group. Duplicate rows (same key+offset) in the
  // same bucket are the same mapping — what matters is that different cars
  // don't end up at the same address.
  //
  // 1. Dedup rows by their physical fingerprint (same key + same row data).
  const seenRowKeys = new Set<string>();
  const uniqueRows: MappingRowForDetection[] = [];
  rows.forEach((row) => {
    const rowKey = `${row.ln2MessageKey}|${row.offset}|${row.byte_offset}|${row.bit_offset}|${row.data}|${row.contentType}`;
    if (!seenRowKeys.has(rowKey)) {
      seenRowKeys.add(rowKey);
      uniqueRows.push(row);
    }
  });
  // 2. For each unique row, verify all cars produce distinct physical addresses.
  //    Use mkAddrKey without unitIdx (→ "*") so the check is on absolute byte
  //    position only — delta=0 means all cars share the same register (not resolved).
  const allCarsDistinct = uniqueRows.every((row) => {
    const parsed = parseCompositeKey(row.ln2MessageKey);
    if (!parsed) return false;
    const colKey = resolveColKey(row, parsed.categoryKey);
    const vals = adapterMatrix[colKey] ?? { initial: 0, delta: 0 };
    const carAddresses = new Set<string>();
    return Array.from({ length: numCars }, (_, u) => u).every((u) => {
      const baseOffset = row.contentType === "bit" ? row.byte_offset : row.offset;
      const absByte = baseOffset + vals.initial + u * vals.delta;
      const addrKey = mkAddrKey(row, absByte); // no unitIdx → check physical uniqueness
      if (carAddresses.has(addrKey)) return false;
      carAddresses.add(addrKey);
      return true;
    });
  });
  return uniqueRows.length > 0 && allCarsDistinct;
}

/**
 * Check whether all rows have DISTINCT absolute byte positions at unit 0
 * (initial offset only, no car delta) in the adapter matrix.
 *
 * Used to detect adapter-matrix conflicts for bank_level rows: if two bank_level
 * rows share the same initial offset they genuinely conflict right now regardless
 * of how the bank matrix is later configured. If they have distinct initial offsets
 * they are differentiated at the adapter level and the bank matrix will further
 * separate them per-lift — no current conflict.
 */
function checkAdapterInitialConflicts(
  rows: ReadonlyArray<MappingRowForDetection>,
  adapterMatrix: Record<string, { initial: number; delta: number }>,
): boolean {
  const addrSet = new Set<string>();
  return rows.every((row) => {
    const parsed = parseCompositeKey(row.ln2MessageKey);
    if (!parsed) return false;
    const colKey = resolveColKey(row, parsed.categoryKey);
    const vals = adapterMatrix[colKey] ?? { initial: 0, delta: 0 };
    const baseOffset = row.contentType === "bit" ? row.byte_offset : row.offset;
    const absPos = baseOffset + vals.initial;
    const posKey =
      row.contentType === "bit"
        ? `${absPos}:bit${row.bit_offset}:${row.data}`
        : `${absPos}:byte:${row.value}`;
    if (addrSet.has(posKey)) return false; // two rows map to identical adapter-level position
    addrSet.add(posKey);
    return true;
  });
}

function checkBankMatrixResolves(
  rows: ReadonlyArray<MappingRowForDetection>,
  bankMatrixPerLift: ReadonlyArray<Record<string, number>>,
  numCars: number,
): boolean {
  if (bankMatrixPerLift.length === 0) return false;
  const uniqueKeys = [...new Set(rows.map((r) => r.ln2MessageKey))];
  return uniqueKeys.every((key) => {
    const offsets = bankMatrixPerLift.slice(0, numCars).map((liftMatrix) => liftMatrix[key]);
    if (offsets.some((o) => o === undefined)) return false;
    return new Set(offsets).size === offsets.length;
  });
}

// ---------------------------------------------------------------------------
// detectMappingRowOverlaps
// ---------------------------------------------------------------------------

export function detectMappingRowOverlaps(input: MappingRowDetectionInput): Set<number> {
  const { mappings, adapterMatrix, bankMatrixPerLift, numCars, intra, directionAware } = input;

  const red = new Set<number>();
  const keyToIndices = new Map<string, number[]>();

  mappings.forEach((row, i) => {
    if (row.is_pair_secondary) return;
    const catKey = row.ln2MessageKey.split(":")[0] ?? "";
    const bucketIntra = intraForRow(row, intra);

    let key: string;
    if (bucketIntra) {
      if (row.contentType === "bit") {
        key = `bit:${row.byte_offset}:${row.bit_offset}:${row.data}`;
      } else {
        key = `byte:${row.offset}:${row.value}`;
      }
    } else if ((row.individual || row.bank_level) && row.ln2MessageKey.includes(":")) {
      // Both individual and bank_level rows have their own adapter-matrix column
      // keyed by the full ln2MessageKey (see resolveColKey). Two rows for different
      // messages (e.g. Traffic::Car Call vs Traffic::Rear Car Call, or
      // Control::Energize Car Call vs Control::Car To Lobby) with the same relative
      // byte/bit must NOT share a bucket — their distinct columns give them distinct
      // absolute positions. Only rows with the exact same ln2MessageKey+offset can
      // genuinely duplicate each other.
      if (row.contentType === "bit") {
        key = `${row.ln2MessageKey}:bit:${row.byte_offset}:${row.bit_offset}:${row.data}`;
      } else {
        key = `${row.ln2MessageKey}:byte:${row.offset}:${row.value}`;
      }
    } else if (row.contentType === "bit") {
      key = `${catKey}:bit:${row.byte_offset}:${row.bit_offset}:${row.data}`;
    } else {
      key = `${catKey}:byte:${row.offset}:${row.value}`;
    }

    const arr = keyToIndices.get(key) ?? [];
    arr.push(i);
    keyToIndices.set(key, arr);
  });

  keyToIndices.forEach((indices) => {
    if (indices.length < 2) return;
    const rowsInBucket = indices.map((i) => mappings[i]);

    // Rows sharing an address do not collide if they claim different LN1 maps
    // — one written TO the device, the other read FROM it. This is the case
    // the direction feature exists for. Guarded on directionAware so that an
    // adapter which has not opted in keeps byte-identical detection, and
    // placed before the matrix-resolution branches below so a legal split is
    // never mistaken for an unresolved overlap.
    if (directionAware && bucketClaimsAreDisjoint(rowsInBucket)) return;
    const isResolvedByAdapterMatrix = checkAdapterMatrixResolves(
      rowsInBucket,
      adapterMatrix,
      numCars,
    );
    const anyBankLevel = rowsInBucket.some((r) => r.bank_level === true);
    const anyIndividual = rowsInBucket.some((r) => r.individual === true && r.bank_level !== true);

    if (anyBankLevel && anyIndividual) {
      // Branch 1: mixed bucket — resolve bank_level and individual rows independently.
      //
      // bank_level rows: check whether their adapter-matrix initial offsets are
      // distinct (checkAdapterInitialConflicts). If two bank_level rows share the
      // same initial offset they conflict NOW regardless of bank matrix config.
      // If their initials are all unique, the bank matrix will further differentiate
      // per-lift — no current conflict at the adapter level.
      const bankRows = rowsInBucket.filter((r) => r.bank_level);
      const bankInitialOk = checkAdapterInitialConflicts(bankRows, adapterMatrix);
      if (!bankInitialOk) {
        indices.filter((i) => mappings[i].bank_level).forEach((idx) => red.add(idx));
      }

      // individual rows: each must produce distinct absolute addresses per car.
      const indivRows = rowsInBucket.filter((r) => r.individual && !r.bank_level);
      if (indivRows.length > 0) {
        const indivResolved = checkAdapterMatrixResolves(indivRows, adapterMatrix, numCars);
        if (!indivResolved) {
          indices
            .filter((i) => mappings[i].individual && !mappings[i].bank_level)
            .forEach((idx) => red.add(idx));
        }
      }
    } else if (anyBankLevel) {
      // Branch 2: bank-level only — ONLY bank matrix can resolve per-lift conflicts.
      const bankResolved = checkBankMatrixResolves(rowsInBucket, bankMatrixPerLift, numCars);
      if (!bankResolved) {
        indices.forEach((idx) => red.add(idx));
      }
      // Plain (non-bank, non-individual) rows in the same bucket are genuine
      // duplicates that cannot be resolved — flag them.
      const plainIndices = indices.filter(
        (i) => !mappings[i].bank_level && !mappings[i].individual,
      );
      if (plainIndices.length > 1) {
        plainIndices.forEach((idx) => red.add(idx));
      }
    } else if (anyIndividual) {
      // Branch 3: individual only → ONLY adapter matrix can resolve
      if (!isResolvedByAdapterMatrix) {
        indices.forEach((idx) => {
          red.add(idx);
        });
      }
    } else {
      // Branch 4: neither flag → nothing resolves, stays red
      indices.forEach((idx) => {
        red.add(idx);
      });
    }
  });

  return red;
}

// ---------------------------------------------------------------------------
// formatOverlapTooltip
// ---------------------------------------------------------------------------

export function formatOverlapTooltip(info: OverlapInfo): string {
  const aRange = `[${info.a.absByte}, ${info.a.absByte + info.a.size})`;
  const bRange = `[${info.b.absByte}, ${info.b.absByte + info.b.size})`;
  const bytesPart =
    info.overlappingBytes.length === 1
      ? `overlapping byte: ${info.overlappingBytes[0]}`
      : `overlapping bytes: ${info.overlappingBytes.join(", ")}`;
  let bitsPart = "";
  if (info.overlappingBits !== undefined && info.overlappingBits.length > 0) {
    const bitLabel = info.overlappingBits.length === 1 ? "bit" : "bits";
    bitsPart = ` ${bitLabel} ${info.overlappingBits.join(", ")}`;
  }
  let liftsPart = "";
  if (info.crossUnit) {
    liftsPart = ` (cross-lift bleed from lift ${info.a.unitIdx + 1} into lift ${
      info.b.unitIdx + 1
    })`;
  }
  return `${info.a.label} ${aRange} overlaps ${info.b.label} ${bRange}; ${bytesPart}${bitsPart}${liftsPart}`;
}
